import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProgressBar } from "primereact/progressbar";
import { useAuth } from "context/AuthContext";
import { useNotification } from "context/NotificationContext";
import Api from "utils/Api";
import * as XLSX from "xlsx";

const ImportarCfop = () => {
  const [matrizes, setMatrizes] = useState([]);
  const [selectedMatriz, setSelectedMatriz] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [validationErrorsList, setValidationErrorsList] = useState([]);
  const navigate = useNavigate();
  const auth = useAuth();
  const notify = useNotification();
  const Requicicao = useMemo(() => new Api(), []);

  useEffect(() => {
    const fetchMatrizes = async () => {
      try {
        const resposta = await Requicicao.Get({
          endpoint: "/Empresa/ObterMatrizesUsuarioAutenticado",
          config: auth.GetHeaders(),
        });

        if (resposta.codigoEstadoHttp === 200) {
          const matrizesFormatadas = resposta.objetoResposta.map((matriz) => ({
            label: `${matriz.nomeCompletoOuRazaoSocial} (${matriz.apelidoOuNomeFantasia})`,
            value: matriz.id,
          }));
          setMatrizes(matrizesFormatadas);
        }
      } catch (error) {
        console.error("Erro ao buscar matrizes:", error);
        notify({
          type: "erro",
          message: "Erro ao carregar matrizes. Tente novamente.",
        });
      }
    };

    fetchMatrizes();
  }, [auth, Requicicao]);

  const validateExcel = (worksheet) => {
    const requiredColumns = ["Codigo/SKU", "CFOP"];
    const firstRow = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
    const errors = [];

    // Verificar se as colunas obrigatórias existem
    requiredColumns.forEach((column) => {
      if (!firstRow.includes(column)) {
        errors.push(`Coluna obrigatória ausente: ${column}`);
      }
    });

    // Verificar se a ordem das colunas está correta
    const isOrderCorrect = 
      firstRow[0] === requiredColumns[0] && 
      firstRow[1] === requiredColumns[1];

    if (!isOrderCorrect) {
      return errors;
    }

    // Verificar se há campos vazios
    const rows = XLSX.utils.sheet_to_json(worksheet);
    rows.forEach((row, rowIndex) => {
      requiredColumns.forEach((column) => {
        if (!row[column]) {
          errors.push(`O campo ${column} está vazio na linha ${rowIndex + 2}.`);
        }
      });
    });

    return errors;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setValidationError("");
    setValidationErrorsList([]);
  };

  const handleUpload = async () => {
    setValidationError("");

    if (!file || !selectedMatriz) {
      notify({
        type: "erro",
        message: "Arquivo e Matriz são obrigatórios.",
      });
      return;
    }

    try {
      // Ler o arquivo Excel e validar as colunas
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const validationErrors = validateExcel(worksheet);
      if (validationErrors.length > 0) {
        setValidationErrorsList(validationErrors);
        setValidationError(validationErrors.join("\n"));
        setFile(null);
        return;
      }

      // Contar a quantidade de registros no Excel
      const rowCount = XLSX.utils.sheet_to_json(worksheet).length;

      setTotalRecords(rowCount);
      setProgress(0);
      setShowConfirmDialog(true);
    } catch (error) {
      setValidationError("Erro ao processar o arquivo. Tente novamente.");
      setFile(null);
    }
  };

  const downloadErrors = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      validationErrorsList.map((error) => ({ Error: error }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Erros");
    XLSX.writeFile(workbook, "Erros_Importacao_CFOP.xlsx");
  };

  const confirmUpload = async () => {
    setShowConfirmDialog(false);
    setIsProcessing(true);

    const formData = new FormData();
    formData.append("arquivo", file);
    formData.append("empresaMatrizId", selectedMatriz);
    formData.append("atualizarSeExistir", "true");

    const intervalTime = 90000 / totalRecords;
    let simulatedProgress = 0;

    const interval = setInterval(() => {
      simulatedProgress += 1;
      setProgress(Math.min(simulatedProgress, totalRecords));

      if (simulatedProgress >= totalRecords) {
        clearInterval(interval);
      }
    }, intervalTime);

    try {
      const response = await fetch(
        `${Requicicao.baseURL}/Produto/ImportarCfop`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user?.token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        clearInterval(interval);
        setProgress(100);
        
        setTimeout(() => {
          setIsProcessing(false);
          notify({
            type: "sucesso",
            message: data.mensagemAdicional || "Atualização concluída com sucesso!",
          });
          setFile(null);
          setProgress(0);
          navigate("/app/produto");
        }, 1000);
      } else {
        clearInterval(interval);
        setIsProcessing(false);
        const errorData = await response.text();
        notify({
          type: "erro",
          message: errorData || "Erro na importação. Tente novamente.",
        });
      }
    } catch (error) {
      clearInterval(interval);
      setIsProcessing(false);
      console.error("Erro na importação:", error);
      notify({
        type: "erro",
        message: "Erro na importação. Tente novamente.",
      });
    }
  };

  const downloadTemplate = () => {
    const template = [
      ["Codigo/SKU", "CFOP"],
      ["PROD001", "5102"],
      ["PROD002", "5102"],
      ["PROD003", "1202"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "Template_Importacao_CFOP.xlsx");
  };

  return (
    <div className="card p-4">
      <div
        className="alert alert-info"
        style={{ fontSize: "0.8rem", marginBottom: "1rem" }}
      >
        Nota: O CFOP será atualizado para os produtos existentes na matriz selecionada.
      </div>

      {isProcessing ? (
        <div className="flex justify-content-center align-items-center">
          <ProgressSpinner />
          <div style={{ width: "100%", marginTop: "1rem" }}>
            <ProgressBar value={Math.round((progress / totalRecords) * 100)} />
            <p>
              Atualizando {Math.min(progress, totalRecords)} de {totalRecords}{" "}
              registros...
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <label htmlFor="matriz" className="form-label me-2">
              Selecione a Matriz: 
            </label>
            <Dropdown
              id="matriz"
              value={selectedMatriz}
              options={matrizes}
              onChange={(e) => setSelectedMatriz(e.value)}
              placeholder="Selecione a Matriz"
              className="w-full"
              style={{ fontSize: "0.8rem" }}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="arquivo" className="form-label me-2">
              Selecione o Arquivo Excel
            </label>
            <input
              type="file"
              id="arquivo"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="form-control"
              style={{ fontSize: "0.8rem" }}
            />
          </div>

          {validationError && (
            <div className="alert alert-danger" style={{ fontSize: "0.8rem" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  label="Baixar Erros"
                  icon="pi pi-download"
                  className="mt-2"
                  style={{ fontSize: "0.8rem" }}
                  onClick={downloadErrors}
                />
              </div>
              {validationError.split("\n").map((error) => (
                  <p key={error}>{error}</p>
              ))}              
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <Button
              label="Atualizar CFOP"
              icon="pi pi-refresh"
              style={{ fontSize: "0.8rem" }}
              onClick={handleUpload}
              disabled={isProcessing || !file || !selectedMatriz}
            />
            <Button
              label="Baixar Template"
              icon="pi pi-download"
              style={{ fontSize: "0.8rem" }}
              onClick={downloadTemplate}
            />
          </div>

          <div
            style={{ fontSize: "0.7rem", color: "#888", marginTop: "0.5rem" }}
          >
            * Certifique-se de que os CFOPs estejam corretos após a atualização.
          </div>
        </>
      )}

      <Dialog
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        header="Confirmação de Atualização"
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setShowConfirmDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={confirmUpload}
              autoFocus
            />
          </div>
        }
      >
        <p>
          Ao prosseguir, os CFOPs dos produtos existentes para a matriz selecionada serão
          atualizados. Deseja continuar?
        </p>
      </Dialog>
    </div>
  );
};

export default ImportarCfop;
